import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Tables } from "@/integrations/supabase/types";

export type CommentRow = Tables<"post_comments">;

export function usePostComments(postId: string | null) {
  const qc = useQueryClient();
  useEffect(() => {
    if (!postId) return;
    const subscriptionId = globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const channel = supabase
      .channel(`post-comments-${postId}-${subscriptionId}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "post_comments", filter: `post_id=eq.${postId}` }, () => {
        void qc.invalidateQueries({ queryKey: ["post-comments", postId] });
        void qc.invalidateQueries({ queryKey: ["post-comment-counts"] });
      })
      .subscribe();
    return () => { void channel.unsubscribe(); void supabase.removeChannel(channel); };
  }, [postId, qc]);

  return useQuery({
    queryKey: ["post-comments", postId],
    enabled: !!postId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("post_comments")
        .select("*")
        .eq("post_id", postId!)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return (data ?? []) as CommentRow[];
    },
  });
}

export function useCommentCounts(postIds: string[]) {
  const key = postIds.slice().sort().join(",");
  return useQuery({
    queryKey: ["post-comment-counts", key],
    enabled: postIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("post_comments")
        .select("post_id")
        .in("post_id", postIds);
      if (error) throw error;
      const counts: Record<string, number> = {};
      (data ?? []).forEach((r) => { counts[r.post_id] = (counts[r.post_id] ?? 0) + 1; });
      return counts;
    },
  });
}

export function useAddComment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ postId, authorId, body, mentions }: { postId: string; authorId: string; body: string; mentions: string[] }) => {
      const { data, error } = await supabase
        .from("post_comments")
        .insert({ post_id: postId, author_id: authorId, body, mentions })
        .select()
        .single();
      if (error) throw error;
      return data as CommentRow;
    },
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: ["post-comments", row.post_id] });
      qc.invalidateQueries({ queryKey: ["post-comment-counts"] });
    },
  });
}

export function useDeleteComment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("post_comments").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["post-comments"] });
      qc.invalidateQueries({ queryKey: ["post-comment-counts"] });
    },
  });
}
